import React, { useState, useEffect } from 'react';
import { getDocs, addDoc, updateDoc, deleteDoc, doc, collection } from 'firebase/firestore';
import { db } from '../firebase/config';
import { toast } from 'react-toastify';

export default function DiscountManagement() {
  const [discounts, setDiscounts] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(null);

  const getCategoryDisplayName = React.useCallback((cat) => {
    if (!cat) return '';
    return (cat.nameEn || cat.name || cat.nameAr || '').toString().trim();
  }, []);

  const categoriesById = React.useMemo(() => {
    const map = new Map();
    categories.forEach(c => {
      map.set(c.id, c);
    });
    return map;
  }, [categories]);

  const formatCategoryLabel = React.useCallback((cat) => {
    const name = getCategoryDisplayName(cat) || 'Unnamed';
    if (cat?.parentId) {
      const parent = categoriesById.get(cat.parentId);
      const parentName = getCategoryDisplayName(parent) || 'Unknown Parent';
      return `${parentName} > ${name}`;
    }
    return `Main: ${name}`;
  }, [categoriesById, getCategoryDisplayName]);

  const sortedCategoryGroups = React.useMemo(() => {
    const parents = categories.filter(c => !c.parentId);
    const children = categories.filter(c => !!c.parentId);

    const byName = (a, b) => getCategoryDisplayName(a).localeCompare(getCategoryDisplayName(b));
    parents.sort(byName);

    children.sort((a, b) => {
      const aParent = categoriesById.get(a.parentId);
      const bParent = categoriesById.get(b.parentId);
      const aParentName = getCategoryDisplayName(aParent);
      const bParentName = getCategoryDisplayName(bParent);
      const parentCmp = aParentName.localeCompare(bParentName);
      if (parentCmp !== 0) return parentCmp;
      return getCategoryDisplayName(a).localeCompare(getCategoryDisplayName(b));
    });

    // Any child whose parent is missing still shows under subcategories.
    return { parents, children };
  }, [categories, categoriesById, getCategoryDisplayName]);
  
  const [formData, setFormData] = useState({
    name: '',
    type: 'percentage', // percentage, fixed
    value: '',
    targetType: 'all', // all, product, category, coupon
    targetId: '',
    couponCode: '', // For coupon discounts
    minAmount: '',
    maxUses: '',
    usedCount: 0, // Track usage
    expiryDate: '',
    isActive: true,
    isPermanent: false // For permanent discounts
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [discountsSnap, productsSnap, categoriesSnap] = await Promise.all([
        getDocs(collection(db, 'discounts')),
        getDocs(collection(db, 'products')),
        getDocs(collection(db, 'categories'))
      ]);

      const discountsData = discountsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const productsData = productsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const categoriesData = categoriesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      setDiscounts(discountsData);
      setProducts(productsData);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const discountData = {
        ...formData,
        value: Number(formData.value),
        minAmount: formData.minAmount ? Number(formData.minAmount) : null,
        maxUses: formData.maxUses ? Number(formData.maxUses) : null,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      if (editingDiscount) {
        await updateDoc(doc(db, 'discounts', editingDiscount.id), discountData);
        toast.success('Discount updated successfully');
      } else {
        await addDoc(collection(db, 'discounts'), discountData);
        toast.success('Discount created successfully');
      }

      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving discount:', error);
      toast.error('Failed to save discount');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'percentage',
      value: '',
      targetType: 'all',
      targetId: '',
      couponCode: '',
      minAmount: '',
      maxUses: '',
      usedCount: 0,
      expiryDate: '',
      isActive: true,
      isPermanent: false
    });
    setShowForm(false);
    setEditingDiscount(null);
  };

  const handleEdit = (discount) => {
    setFormData({
      name: discount.name || '',
      type: discount.type || 'percentage',
      value: discount.value || '',
      targetType: discount.targetType || 'all',
      targetId: discount.targetId || '',
      couponCode: discount.couponCode || '',
      minAmount: discount.minAmount || '',
      maxUses: discount.maxUses || '',
      usedCount: discount.usedCount || 0,
      expiryDate: discount.expiryDate || '',
      isActive: discount.isActive !== false,
      isPermanent: discount.isPermanent || false
    });
    setEditingDiscount(discount);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this discount?')) {
      try {
        await deleteDoc(doc(db, 'discounts', id));
        toast.success('Discount deleted successfully');
        fetchData();
      } catch (error) {
        console.error('Error deleting discount:', error);
        toast.error('Failed to delete discount');
      }
    }
  };

  const toggleDiscountStatus = async (discount) => {
    try {
      await updateDoc(doc(db, 'discounts', discount.id), {
        isActive: !discount.isActive,
        updatedAt: new Date()
      });
      toast.success(`Discount ${!discount.isActive ? 'activated' : 'deactivated'} successfully`);
      fetchData();
    } catch (error) {
      console.error('Error toggling discount:', error);
      toast.error('Failed to update discount');
    }
  };

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>;
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <div>
          <h2>Discount Management</h2>
          <p>Create and manage discounts and coupons</p>
        </div>
        <button 
          className="btn-primary"
          onClick={() => {
            resetForm();
            setShowForm(!showForm);
          }}
        >
          <ion-icon name={showForm ? "close-outline" : "add-outline"}></ion-icon>
          {showForm ? 'Cancel' : 'Add New Discount'}
        </button>
      </div>

      {showForm && (
        <div className="admin-card">
          <div className="card-header">
            <h3>{editingDiscount ? 'Edit Discount' : 'Create New Discount'}</h3>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="input-group">
                <label>Discount Name</label>
                <input
                  type="text"
                  className="custom-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="e.g., Summer Sale"
                />
              </div>

              <div className="input-group">
                <label>Discount Type</label>
                <select
                  className="custom-select"
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                >
                  <option value="percentage">Percentage (%)</option>
                  <option value="fixed">Fixed Amount (EGP)</option>
                </select>
              </div>

              <div className="input-group">
                <label>Value</label>
                <input
                  type="number"
                  className="custom-input"
                  value={formData.value}
                  onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                  required
                  min="0"
                  step={formData.type === 'percentage' ? '1' : '0.01'}
                  placeholder={formData.type === 'percentage' ? 'e.g., 20' : 'e.g., 100'}
                />
              </div>

              <div className="input-group">
                <label>Target Type</label>
                <select
                  className="custom-select"
                  value={formData.targetType}
                  onChange={(e) => setFormData({ ...formData, targetType: e.target.value, targetId: '' })}
                >
                  <option value="all">All Products</option>
                  <option value="category">Specific Category</option>
                  <option value="product">Specific Product</option>
                  <option value="coupon">Coupon Code</option>
                </select>
              </div>

              {formData.targetType === 'category' && (
                <div className="input-group">
                  <label>Select Category</label>
                  <select
                    className="custom-select"
                    value={formData.targetId}
                    onChange={(e) => setFormData({ ...formData, targetId: e.target.value })}
                    required
                  >
                    <option value="">Select a category...</option>
                    {sortedCategoryGroups.parents.length > 0 && (
                      <optgroup label="Main Categories">
                        {sortedCategoryGroups.parents.map(cat => (
                          <option key={cat.id} value={cat.id}>
                            {formatCategoryLabel(cat)}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {sortedCategoryGroups.children.length > 0 && (
                      <optgroup label="Subcategories">
                        {sortedCategoryGroups.children.map(cat => (
                          <option key={cat.id} value={cat.id}>
                            {formatCategoryLabel(cat)}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                </div>
              )}

              {formData.targetType === 'product' && (
                <div className="input-group">
                  <label>Select Product</label>
                  <select
                    className="custom-select"
                    value={formData.targetId}
                    onChange={(e) => setFormData({ ...formData, targetId: e.target.value })}
                    required
                  >
                    <option value="">Select a product...</option>
                    {products.map(prod => (
                      <option key={prod.id} value={prod.id}>{prod.nameEn}</option>
                    ))}
                  </select>
                </div>
              )}

              {formData.targetType === 'coupon' && (
                <div className="input-group">
                  <label>Coupon Code</label>
                  <input
                    type="text"
                    className="custom-input"
                    value={formData.couponCode}
                    onChange={(e) => setFormData({ ...formData, couponCode: e.target.value.toUpperCase() })}
                    required
                    placeholder="e.g., SUMMER20, WELCOME50"
                  />
                </div>
              )}

              <div className="input-group">
                <label>Minimum Amount (EGP)</label>
                <input
                  type="number"
                  className="custom-input"
                  value={formData.minAmount}
                  onChange={(e) => setFormData({ ...formData, minAmount: e.target.value })}
                  min="0"
                  step="0.01"
                  placeholder="Optional minimum order amount"
                />
              </div>

              <div className="input-group">
                <label>Maximum Uses</label>
                <input
                  type="number"
                  className="custom-input"
                  value={formData.maxUses}
                  onChange={(e) => setFormData({ ...formData, maxUses: e.target.value })}
                  min="1"
                  placeholder="Optional usage limit"
                />
              </div>

              <div className="input-group">
                <label>Duration</label>
                <select
                  className="custom-select"
                  value={formData.isPermanent ? 'permanent' : 'temporary'}
                  onChange={(e) => setFormData({ ...formData, isPermanent: e.target.value === 'permanent', expiryDate: e.target.value === 'permanent' ? '' : formData.expiryDate })}
                >
                  <option value="temporary">Temporary (with expiry date)</option>
                  <option value="permanent">Permanent (no expiry)</option>
                </select>
              </div>

              {!formData.isPermanent && (
                <div className="input-group">
                  <label>Expiry Date</label>
                  <input
                    type="datetime-local"
                    className="custom-input"
                    value={formData.expiryDate}
                    onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
                  />
                </div>
              )}

              <div className="input-group" style={{display: 'flex', alignItems: 'center', gap: '10px', marginTop: '30px'}}>
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  style={{ width: '20px', height: '20px' }}
                />
                <label htmlFor="isActive" style={{marginBottom: 0}}>
                  Active
                </label>
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                onClick={resetForm}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
              >
                {editingDiscount ? 'Update Discount' : 'Create Discount'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="admin-card" style={{padding: 0, overflow: 'hidden'}}>
        <div className="card-header" style={{padding: '20px', borderBottom: '1px solid #e1e3e5', marginBottom: 0}}>
            <h3>Active Discounts</h3>
        </div>
        {discounts.length === 0 ? (
          <div style={{padding: '40px', textAlign: 'center', color: '#6b7280'}}>
            <p>No discounts found. Create your first discount to get started.</p>
          </div>
        ) : (
          <div className="table-container" style={{border: 'none'}}>
            <table className="admin-table">
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Type</th>
                        <th>Target</th>
                        <th>Status</th>
                        <th style={{textAlign: 'right'}}>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {discounts.map(discount => (
                        <tr key={discount.id}>
                            <td>
                                <div style={{fontWeight: 500, color: '#111827'}}>{discount.name}</div>
                                {discount.expiryDate && (
                                    <div style={{fontSize: '0.75rem', color: '#6b7280'}}>
                                        Expires: {new Date(discount.expiryDate).toLocaleDateString()}
                                    </div>
                                )}
                            </td>
                            <td>
                                <span style={{fontWeight: 600, color: '#059669'}}>
                                    {discount.type === 'percentage' ? `${discount.value}% OFF` : `EGP ${discount.value} OFF`}
                                </span>
                            </td>
                            <td>
                                {discount.targetType === 'all' && <span className="badge">All Products</span>}
                                {discount.targetType === 'category' && (
                                  <span className="badge">
                                    Category: {formatCategoryLabel(categoriesById.get(discount.targetId)) || 'Unknown'}
                                  </span>
                                )}
                                {discount.targetType === 'product' && <span className="badge">Product: {products.find(p => p.id === discount.targetId)?.nameEn || 'Unknown'}</span>}
                                {discount.targetType === 'coupon' && <span className="badge" style={{backgroundColor: '#e0e7ff', color: '#4338ca'}}>Code: {discount.couponCode}</span>}
                            </td>
                            <td>
                                <span style={{
                                    padding: '4px 8px', 
                                    borderRadius: '12px', 
                                    fontSize: '0.75rem', 
                                    fontWeight: 600,
                                    backgroundColor: discount.isActive ? '#def7ec' : '#fde8e8',
                                    color: discount.isActive ? '#03543f' : '#9b1c1c'
                                }}>
                                    {discount.isActive ? 'Active' : 'Inactive'}
                                </span>
                            </td>
                            <td style={{textAlign: 'right'}}>
                                <button
                                    onClick={() => toggleDiscountStatus(discount)}
                                    className="action-btn"
                                    title={discount.isActive ? 'Deactivate' : 'Activate'}
                                >
                                    <ion-icon name={discount.isActive ? "pause-outline" : "play-outline"}></ion-icon>
                                </button>
                                <button
                                    onClick={() => handleEdit(discount)}
                                    className="action-btn edit"
                                    title="Edit"
                                >
                                    <ion-icon name="create-outline"></ion-icon>
                                </button>
                                <button
                                    onClick={() => handleDelete(discount.id)}
                                    className="action-btn delete"
                                    title="Delete"
                                >
                                    <ion-icon name="trash-outline"></ion-icon>
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
